import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronLeft, ChevronRight, Search, X } from 'lucide-react';

import { supplyService, type ProductionWeekTrayItem, type SupplyTableFilters } from '@/services/supplyService';
import {
  PRODUCTION_WEEK_PAGE_SIZE,
  addIsoWeeks,
  defaultWeekWindowStart,
  firstIsoMondayOfYear,
  formatWeekDateRange,
  isoMondayFromDate,
  isoWeekParts,
  lastIsoMondayOfYear,
  listIsoMondaysInYear,
  parseIsoDateLocal,
  productionWeekChipStatus,
  toIsoDate,
  windowStartContainingWeek,
  type ProductionWeekChipStatus,
} from '@/utils/productionScope';

interface ProductionScopeSelectorsProps {
  productionWeek: string;
  soExternalId: string;
  warehouseId?: number | null;
  applyMoqMp?: boolean;
  refreshNonce?: number;
  onWeekChange: (week: string) => void;
  onSoExternalIdChange: (soExternalId: string) => void;
}

type SearchableOption = {
  value: string;
  label: string;
  searchText: string;
};

const CHIP_CLASS: Record<ProductionWeekChipStatus, string> = {
  empty: 'border-gray-200 bg-gray-100 text-gray-500 hover:bg-gray-200',
  covered: 'border-emerald-300 bg-emerald-100 text-emerald-950 hover:bg-emerald-200',
  pending: 'border-amber-300 bg-amber-100 text-amber-950 hover:bg-amber-200',
  urgent: 'border-red-300 bg-red-100 text-red-950 hover:bg-red-200',
};

function matchesSearch(option: SearchableOption, query: string): boolean {
  if (!query) return true;
  return option.searchText.includes(query);
}

function SearchableSelect({
  value,
  options,
  placeholder,
  searchPlaceholder,
  disabled,
  onChange,
  onOpen,
}: {
  value: string;
  options: SearchableOption[];
  placeholder: string;
  searchPlaceholder: string;
  disabled?: boolean;
  onChange: (value: string) => void;
  onOpen?: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const selected = options.find((option) => option.value === value);
  const normalizedQuery = query.trim().toLowerCase();
  const filtered = useMemo(
    () => options.filter((option) => matchesSearch(option, normalizedQuery)),
    [options, normalizedQuery],
  );

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery('');
      requestAnimationFrame(() => searchRef.current?.focus());
    }
  }, [open]);

  const choose = (next: string) => {
    onChange(next);
    setOpen(false);
  };

  return (
    <div ref={rootRef} className="relative w-full min-w-0 max-w-full sm:max-w-[22rem]">
      <div
        className={`flex h-9 w-full min-w-0 items-center rounded-md border border-gray-300 bg-white text-sm focus-within:border-[#1681ba] focus-within:ring-1 focus-within:ring-[#1681ba] ${
          disabled ? 'cursor-not-allowed bg-gray-100 text-gray-400' : 'text-gray-800'
        }`}
      >
        <button
          type="button"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          className="flex min-w-0 flex-1 items-center justify-between gap-2 px-2 text-left disabled:cursor-not-allowed"
          onClick={() => {
            if (disabled) return;
            setOpen((prev) => {
              const next = !prev;
              if (next) onOpen?.();
              return next;
            });
          }}
        >
          <span className={`truncate ${selected ? 'text-gray-900' : 'text-gray-500'}`}>
            {selected?.label || placeholder}
          </span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden />
        </button>
        {value && !disabled ? (
          <button
            type="button"
            className="mr-1 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            aria-label={t('supply.productionScope.clearFilter')}
            onClick={() => onChange('')}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : null}
      </div>

      {open && !disabled ? (
        <div className="absolute z-50 mt-0.5 w-full rounded-md bg-white py-0.5 text-sm shadow-lg ring-1 ring-black/5">
          <div className="border-b border-gray-100 px-2 py-1.5">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
              <input
                ref={searchRef}
                type="search"
                value={query}
                placeholder={searchPlaceholder}
                autoComplete="off"
                className="block w-full rounded border border-gray-200 py-1.5 pl-7 pr-7 text-xs focus:border-[#1681ba] focus:outline-none focus:ring-1 focus:ring-[#1681ba]"
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && filtered.length === 1) {
                    event.preventDefault();
                    choose(filtered[0].value);
                  }
                }}
              />
              {query ? (
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex items-center pr-2"
                  onClick={() => setQuery('')}
                  aria-label={t('filters.clear')}
                >
                  <X className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600" />
                </button>
              ) : null}
            </div>
          </div>
          <ul role="listbox" className="max-h-48 overflow-auto py-0.5">
            <li>
              <button
                type="button"
                role="option"
                aria-selected={!value}
                className={`flex w-full px-2.5 py-1.5 text-left text-xs hover:bg-indigo-50 ${
                  !value ? 'font-medium text-indigo-900' : 'text-gray-600'
                }`}
                onClick={() => choose('')}
              >
                {placeholder}
              </button>
            </li>
            {filtered.length === 0 ? (
              <li className="px-2.5 py-2 text-center text-xs text-gray-500">
                {t('filters.noResults')}
              </li>
            ) : (
              filtered.map((option) => (
                <li key={option.value}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={option.value === value}
                    className={`flex w-full px-2.5 py-1.5 text-left text-xs hover:bg-indigo-50 ${
                      option.value === value ? 'font-medium text-indigo-900' : 'text-gray-800'
                    }`}
                    onClick={() => choose(option.value)}
                  >
                    {option.label}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export function ProductionScopeSelectors({
  productionWeek,
  soExternalId,
  warehouseId,
  applyMoqMp = true,
  refreshNonce = 0,
  onWeekChange,
  onSoExternalIdChange,
}: ProductionScopeSelectorsProps) {
  const { t, i18n } = useTranslation();
  const todayMonday = useMemo(() => isoMondayFromDate(new Date()), []);
  const currentYear = isoWeekParts(todayMonday).year;
  const [year, setYear] = useState(currentYear);
  const [windowStart, setWindowStart] = useState(() => defaultWeekWindowStart(currentYear, todayMonday));
  const [statusByWeek, setStatusByWeek] = useState<Record<string, ProductionWeekChipStatus>>({});
  const [soIds, setSoIds] = useState<string[]>([]);
  const [loadingSo, setLoadingSo] = useState(false);
  const allSoCatalogLoadedRef = useRef(false);
  // Fuente motor: la bandeja se pide por ventana visible (una llamada al motor
  // por semana abierta). Neteo interno: una sola carga con todas las semanas.
  const trayIsEngineRef = useRef(false);
  const trayBaseKeyRef = useRef('');

  const visibleMondays = useMemo(
    () => listIsoMondaysInYear(windowStart, year, PRODUCTION_WEEK_PAGE_SIZE),
    [windowStart, year],
  );
  const visibleFrom = visibleMondays.length ? toIsoDate(visibleMondays[0]) : '';
  const visibleTo = visibleMondays.length ? toIsoDate(visibleMondays[visibleMondays.length - 1]) : '';

  useEffect(() => {
    if (!warehouseId) {
      setStatusByWeek({});
      return;
    }
    const baseKey = `${warehouseId}|${applyMoqMp}|${refreshNonce}`;
    const onlyWindowChanged = trayBaseKeyRef.current === baseKey;
    if (onlyWindowChanged && !trayIsEngineRef.current) return;
    trayBaseKeyRef.current = baseKey;
    let cancelled = false;
    let done = false;
    void (async () => {
      try {
        const data = await supplyService.getProductionWeekTray(
          warehouseId,
          false,
          applyMoqMp,
          visibleFrom && visibleTo ? { week_from: visibleFrom, week_to: visibleTo } : undefined,
        );
        if (cancelled) return;
        trayIsEngineRef.current = data.source === 'engine';
        const covered = new Set(data.covered_weeks ?? []);
        const next: Record<string, ProductionWeekChipStatus> = {};
        for (const column of data.results ?? []) {
          for (const card of column.weeks ?? []) {
            next[card.week] = productionWeekChipStatus(card as ProductionWeekTrayItem, true);
          }
        }
        for (const week of covered) {
          if (!next[week]) {
            next[week] = productionWeekChipStatus(null, true);
          }
        }
        if (data.source === 'engine' && data.week_from && data.week_to) {
          // Solo se reemplaza la ventana pedida; el resto conserva su color.
          const from = data.week_from;
          const to = data.week_to;
          setStatusByWeek((prev) => {
            const merged: Record<string, ProductionWeekChipStatus> = {};
            for (const [week, chip] of Object.entries(prev)) {
              if (week < from || week > to) merged[week] = chip;
            }
            return { ...merged, ...next };
          });
        } else {
          setStatusByWeek(next);
        }
      } catch {
        if (!cancelled) setStatusByWeek({});
      } finally {
        done = true;
      }
    })();
    return () => {
      cancelled = true;
      // Petición cortada (StrictMode, cambio rápido): la siguiente debe volver a pedir.
      if (!done) trayBaseKeyRef.current = '';
    };
  }, [warehouseId, applyMoqMp, refreshNonce, visibleFrom, visibleTo]);

  useEffect(() => {
    if (!productionWeek) {
      allSoCatalogLoadedRef.current = false;
      return;
    }
    allSoCatalogLoadedRef.current = false;
    let cancelled = false;
    const ac = new AbortController();
    setLoadingSo(true);
    void (async () => {
      try {
        const data: SupplyTableFilters = await supplyService.getSupplyTableFilters({
          signal: ac.signal,
          production_week: productionWeek,
          catalog: 'so',
        });
        if (cancelled) return;
        setSoIds(data.so_external_ids ?? []);
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        if (!cancelled) setSoIds([]);
      } finally {
        if (!cancelled) setLoadingSo(false);
      }
    })();
    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [productionWeek]);

  useEffect(() => {
    if (!productionWeek || loadingSo) return;
    if (soExternalId && !soIds.includes(soExternalId)) {
      onSoExternalIdChange('');
    }
  }, [productionWeek, loadingSo, soExternalId, soIds, onSoExternalIdChange]);

  useEffect(() => {
    if (!productionWeek) return;
    const monday = parseIsoDateLocal(productionWeek);
    if (!monday) return;
    const parts = isoWeekParts(monday);
    setYear(parts.year);
    setWindowStart(windowStartContainingWeek(monday, parts.year, todayMonday));
  }, [productionWeek, todayMonday]);

  const yearOptions = useMemo(() => {
    const years = new Set<number>([currentYear - 1, currentYear, currentYear + 1, year]);
    if (productionWeek) {
      const monday = parseIsoDateLocal(productionWeek);
      if (monday) years.add(isoWeekParts(monday).year);
    }
    return [...years].sort((a, b) => a - b);
  }, [currentYear, year, productionWeek]);

  const firstOfYear = firstIsoMondayOfYear(year);
  const lastOfYear = lastIsoMondayOfYear(year);
  const canGoPrev = windowStart > firstOfYear;
  const lastVisible = visibleMondays[visibleMondays.length - 1];
  const canGoNext = Boolean(lastVisible && lastVisible < lastOfYear);

  const soOptions = useMemo<SearchableOption[]>(() => {
    const ids = soExternalId && !soIds.includes(soExternalId)
      ? [soExternalId, ...soIds]
      : soIds;
    return ids.map((id) => ({
      value: id,
      label: id,
      searchText: id.toLowerCase(),
    }));
  }, [soIds, soExternalId]);

  const loadAllSoCatalog = () => {
    if (productionWeek || loadingSo || allSoCatalogLoadedRef.current) return;
    allSoCatalogLoadedRef.current = true;
    setLoadingSo(true);
    void (async () => {
      try {
        const data: SupplyTableFilters = await supplyService.getSupplyTableFilters({
          catalog: 'so',
        });
        setSoIds(data.so_external_ids ?? []);
      } catch {
        allSoCatalogLoadedRef.current = false;
        setSoIds((prev) => prev);
      } finally {
        setLoadingSo(false);
      }
    })();
  };

  const handleYearChange = (nextYear: number) => {
    setYear(nextYear);
    setWindowStart(defaultWeekWindowStart(nextYear, todayMonday));
  };

  const handleWeekClick = (mondayIso: string) => {
    onWeekChange(productionWeek === mondayIso ? '' : mondayIso);
  };

  return (
    <div className="mb-4 w-full min-w-0 rounded-lg border border-indigo-200 bg-indigo-50/60 px-3 py-3 sm:px-4 sm:py-4">
      <div className="mb-2 flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2">
        <label className="flex shrink-0 items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-indigo-800">
            {t('supply.productionScope.yearLabel')}
          </span>
          <select
            className="h-8 rounded-md border border-gray-300 bg-white px-2.5 text-sm text-gray-800 focus:border-[#1681ba] focus:outline-none focus:ring-1 focus:ring-[#1681ba]"
            value={year}
            onChange={(event) => handleYearChange(Number(event.target.value))}
          >
            {yearOptions.map((optionYear) => (
              <option key={optionYear} value={optionYear}>
                {optionYear}
              </option>
            ))}
          </select>
        </label>
        <span className="text-[11px] font-semibold uppercase tracking-wide text-indigo-800">
          {t('supply.productionScope.weekLabel')}
        </span>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-indigo-900/80 sm:ml-auto">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm bg-gray-300" aria-hidden />
            {t('supply.productionScope.chipEmpty')}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm bg-emerald-400" aria-hidden />
            {t('supply.productionScope.chipCovered')}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm bg-amber-400" aria-hidden />
            {t('supply.productionScope.chipPending')}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm bg-red-400" aria-hidden />
            {t('supply.productionScope.chipUrgent')}
          </span>
        </div>
      </div>

      <div className="flex min-w-0 items-center gap-2">
        <button
          type="button"
          className="shrink-0 rounded-md border border-indigo-200 bg-white p-2 text-indigo-800 hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-40"
          disabled={!canGoPrev}
          aria-label={t('supply.productionScope.prevWeeks')}
          onClick={() => {
            const next = addIsoWeeks(windowStart, -PRODUCTION_WEEK_PAGE_SIZE);
            setWindowStart(next < firstOfYear ? firstOfYear : next);
          }}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <div className="flex min-w-0 flex-1 gap-2 overflow-x-auto pb-0.5 [scrollbar-width:thin]">
          {visibleMondays.map((monday) => {
            const parts = isoWeekParts(monday);
            const status = statusByWeek[parts.mondayIso] ?? 'empty';
            const selected = productionWeek === parts.mondayIso;
            const range = formatWeekDateRange(parts.mondayIso, i18n.language || 'es');
            const statusLabel = t(
              status === 'urgent'
                ? 'supply.productionScope.chipUrgent'
                : status === 'pending'
                  ? 'supply.productionScope.chipPending'
                  : status === 'covered'
                    ? 'supply.productionScope.chipCovered'
                    : 'supply.productionScope.chipEmpty',
            );
            return (
              <button
                key={parts.mondayIso}
                type="button"
                title={`${parts.isoWeek} · ${range.mondayLabel} – ${range.sundayLabel} · ${statusLabel}`}
                aria-pressed={selected}
                className={`w-[3.75rem] shrink-0 rounded-lg border px-1.5 py-2 text-center leading-tight sm:w-[4rem] sm:px-2.5 ${CHIP_CLASS[status]} ${
                  selected ? 'ring-2 ring-indigo-600 ring-offset-1 sm:ring-offset-2' : ''
                }`}
                onClick={() => handleWeekClick(parts.mondayIso)}
              >
                <span className="block text-xs font-semibold">{`W${String(parts.week).padStart(2, '0')}`}</span>
                <span className="mt-0.5 block truncate text-[10px] opacity-80">{range.mondayLabel}</span>
              </button>
            );
          })}
        </div>
        <button
          type="button"
          className="shrink-0 rounded-md border border-indigo-200 bg-white p-2 text-indigo-800 hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-40"
          disabled={!canGoNext}
          aria-label={t('supply.productionScope.nextWeeks')}
          onClick={() => setWindowStart(addIsoWeeks(windowStart, PRODUCTION_WEEK_PAGE_SIZE))}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-3 min-w-0 sm:mt-4">
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-indigo-800">
            {t('supply.productionScope.soLabel')}
          </span>
          <SearchableSelect
            value={soExternalId}
            options={soOptions}
            placeholder={t(
              productionWeek
                ? 'supply.productionScope.soPlaceholder'
                : 'supply.productionScope.soPlaceholderAll',
            )}
            searchPlaceholder={t('supply.productionScope.searchSo')}
            onChange={onSoExternalIdChange}
            onOpen={loadAllSoCatalog}
          />
        </div>
      </div>
    </div>
  );
}
